import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Shield, 
  AlertTriangle,
  CheckCircle,
  User,
  FileText,
  Activity,
  ChevronRight,
  Download,
  TrendingUp,
  TrendingDown,
  Eye
} from 'lucide-react';
import { MobileMetricCard } from '../components/dashboard/MobileMetricCard'
import { useSecurityDashboard } from '../hooks/useSecurity'
import { cn } from '../utils/cn'

interface SecurityModule {
  id: string;
  title: string;
  icon: React.ElementType;
  value: string | number;
  subtitle: string;
  status: 'success' | 'warning' | 'danger' | 'info';
  trend?: {
    value: number;
    direction: 'up' | 'down';
    label: string;
  };
  priority?: 'critical' | 'high' | 'medium' | 'low';
}

export const MobileSecurity: React.FC = () => {
  const [selectedModule, setSelectedModule] = useState<string | null>(null);
  const { data: dashboardData, isLoading } = useSecurityDashboard(30000);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-950">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
      </div>
    );
  }

  const securityModules: SecurityModule[] = [
    {
      id: 'threats',
      title: 'Threat Detection',
      icon: Shield,
      value: dashboardData?.threatDetection?.overview?.activeThreats || 0,
      subtitle: 'Active threats',
      status: (dashboardData?.threatDetection?.overview?.activeThreats ?? 0) > 0 ? 'danger' : 'success',
      priority: 'critical',
    },
    {
      id: 'auth',
      title: 'Authentication',
      icon: User,
      value: `${dashboardData?.authentication?.overview?.successRate?.toFixed(1) || '0'}%`,
      subtitle: 'Success rate',
      status: (dashboardData?.authentication?.overview?.successRate ?? 0) > 95 ? 'success' : 'warning',
      trend: {
        value: 2.3,
        direction: 'up',
        label: 'vs last hour'
      },
      priority: 'high',
    },
    {
      id: 'compliance',
      title: 'Compliance Score',
      icon: CheckCircle,
      value: `${dashboardData?.compliance?.overview?.complianceScore?.toFixed(0) || '0'}%`,
      subtitle: '4 standards met',
      status: (dashboardData?.compliance?.overview?.complianceScore ?? 0) > 90 ? 'success' : 'warning',
      priority: 'medium',
    },
    {
      id: 'identities',
      title: 'Active Identities',
      icon: User,
      value: dashboardData?.identity?.overview?.verifiedIdentities || 0,
      subtitle: `of ${dashboardData?.identity?.overview?.totalIdentities || 0} total`,
      status: 'info',
      priority: 'medium',
    },
    {
      id: 'audit',
      title: 'Audit Events',
      icon: FileText,
      value: dashboardData?.compliance?.overview?.auditEvents?.toLocaleString() || '0',
      subtitle: 'Last 24 hours',
      status: 'info',
      priority: 'high',
    },
  ];

  const overallHealthScore = Math.round(
    ((dashboardData?.authentication?.overview?.successRate || 0) * 0.3 +
     (dashboardData?.compliance?.overview?.complianceScore || 0) * 0.3 +
     ((dashboardData?.threatDetection?.overview?.activeThreats === 0 ? 100 : 50)) * 0.4)
  );

  return (
    <div className="min-h-screen bg-gray-950 pb-20">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-gray-900/95 backdrop-blur-sm border-b border-gray-800">
        <div className="px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gradient-to-r from-red-600 to-orange-600 rounded-lg">
                <Shield className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-white">Security Operations Center</h1>
                <p className="text-xs text-gray-400">Enterprise-grade security monitoring and threat response</p>
              </div>
            </div>
            <button className="p-2 text-gray-400 hover:text-white rounded-lg hover:bg-gray-800 transition-colors">
              <Download className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Security Status Banner */}
      <div className="px-4 py-3">
        <div className="bg-gradient-to-r from-green-600/10 to-blue-600/10 border border-green-600/30 rounded-xl p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-status-online rounded-full animate-pulse" />
              <span className="text-base font-semibold text-white">
                Security Posture: {overallHealthScore > 80 ? 'SECURE' : overallHealthScore > 60 ? 'MODERATE' : 'AT RISK'}
              </span>
            </div>
            <span className="text-sm text-gray-400">
              Last updated: less than a minute ago
            </span>
          </div>
          
          {/* Quick Stats Grid */}
          {/* GH #2704: two cells, not three. The 'Encrypt Ops/sec' cell that
              stood here read dashboardData.cryptography, whose producer -- GET
              /security/crypto/metrics -- was removed as fabricated. Its `|| '0'`
              then rendered a hardcoded zero as measured throughput. Removed to
              match SecurityOperationsCenter, which dropped the crypto view
              outright; nothing replaces it. */}
          <div className="grid grid-cols-2 gap-3 text-center">
            <div className="bg-gray-800/30 rounded-lg p-2">
              <div className="text-lg font-bold text-white">
                {dashboardData?.threatDetection?.overview?.activeThreats || 0}
              </div>
              <div className="text-xs text-gray-400">Active Threats</div>
            </div>
            <div className="bg-gray-800/30 rounded-lg p-2">
              <div className="text-lg font-bold text-white">
                {dashboardData?.authentication?.overview?.successRate?.toFixed(1) || '0'}%
              </div>
              <div className="text-xs text-gray-400">Auth Success</div>
            </div>
          </div>
        </div>
      </div>

      {/* Modules List */}
      <div className="px-4 space-y-3">
        {securityModules.map((module) => (
          <motion.div
            key={module.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className={cn(
              "bg-gray-900/50 backdrop-blur-sm border border-gray-800 rounded-xl overflow-hidden",
              "active:scale-98 transition-transform"
            )}
            onClick={() => setSelectedModule(module.id)}
          >
            <div className="p-4">
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-3 flex-1">
                  <div className={cn(
                    "p-2.5 rounded-lg",
                    module.priority === 'critical' ? 'bg-red-500/20' :
                    module.priority === 'high' ? 'bg-orange-500/20' :
                    module.priority === 'medium' ? 'bg-blue-500/20' :
                    'bg-gray-700/50'
                  )}>
                    <module.icon className={cn(
                      "w-5 h-5",
                      module.priority === 'critical' ? 'text-red-500' :
                      module.priority === 'high' ? 'text-orange-500' :
                      module.priority === 'medium' ? 'text-blue-500' :
                      'text-gray-400'
                    )} />
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <h3 className="text-base font-semibold text-white">{module.title}</h3>
                    <div className="flex items-baseline gap-2 mt-1">
                      <span className={cn(
                        "text-2xl font-bold",
                        module.status === 'success' ? 'text-green-500' :
                        module.status === 'warning' ? 'text-yellow-500' :
                        module.status === 'danger' ? 'text-red-500' :
                        'text-blue-500'
                      )}>
                        {module.value}
                      </span>
                      <span className="text-sm text-gray-400">{module.subtitle}</span>
                    </div>
                    
                    {module.trend && (
                      <div className="flex items-center gap-1 mt-2">
                        {module.trend.direction === 'up' ? (
                          <TrendingUp className="w-4 h-4 text-green-500" />
                        ) : (
                          <TrendingDown className="w-4 h-4 text-red-500" />
                        )}
                        <span className={cn(
                          "text-sm",
                          module.trend.direction === 'up' ? 'text-green-500' : 'text-red-500'
                        )}>
                          {module.trend.value}% {module.trend.label}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
                
                <ChevronRight className="w-5 h-5 text-gray-500 mt-1" />
              </div>
              
              {module.priority && (
                <div className="mt-3 flex items-center justify-between">
                  <span className={cn(
                    "text-xs font-medium px-2 py-1 rounded",
                    module.priority === 'critical' ? 'bg-red-500/20 text-red-400' :
                    module.priority === 'high' ? 'bg-orange-500/20 text-orange-400' :
                    module.priority === 'medium' ? 'bg-blue-500/20 text-blue-400' :
                    'bg-gray-700/50 text-gray-400'
                  )}>
                    {module.priority.toUpperCase()} PRIORITY
                  </span>
                  <button className="text-xs text-blue-500 flex items-center gap-1">
                    <Eye className="w-3 h-3" />
                    View Details
                  </button>
                </div>
              )}
            </div>
          </motion.div>
        ))}
      </div>

      {/* Recent Alerts */}
      <div className="mt-6 px-4">
        <h2 className="text-lg font-semibold text-white mb-3">Recent Security Events</h2>
        <div className="space-y-2">
          {dashboardData?.recentEvents?.threats?.slice(0, 3).map((threat) => (
            <div key={threat.id} className="bg-gray-900/50 backdrop-blur-sm border border-gray-800 rounded-lg p-3">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-orange-500 mt-0.5" />
                <div className="flex-1">
                  <div className="font-medium text-white text-sm">
                    {threat.type.replace('_', ' ').toUpperCase()} detected
                  </div>
                  <div className="text-xs text-gray-400 mt-1">
                    Source: {threat.sourceIP} • {new Date(threat.timestamp).toLocaleTimeString()}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};